import { Injectable, inject } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable, { Styles } from 'jspdf-autotable';
import { Borders, Workbook } from 'exceljs';
import { reportConstants } from '../../common/constants';
import { viscosityReports } from '../../models/lab-test';
import { ViscosityData } from '../../viscosity/models/viscosity';
import { ReportGeneralUtilityService } from '../report.generalUtility.service';
import { reportConstantsMessage } from '../../common/constants';
import { setNumericOrTextCellValue } from './report.cellFormat.util';

@Injectable({
    providedIn: 'root'
})
export class ReportPDFViscosityRenderService {
    private _reportGeneralUtilityService = inject(ReportGeneralUtilityService);

    private commonStylesWithoutBorder = {
        fontSize: 8,
        textColor: [0, 0, 0],
        fillColor: false,
        lineColor: [0, 0, 0],
        lineWidth: 0
    };

    GenerateViscosityPage(doc: jsPDF, marginSize: number, viscosityReport: viscosityReports, appendix = '', wellNameVersion: string, sampleNameVersion: string): void {
        if (viscosityReport !== undefined && viscosityReport !== null) {
            doc.addPage();
            let tableStartY = marginSize + 10;
            doc.setFontSize(14);
            doc.setFont(reportConstants.eMprintCondensedBold);
            doc.text(appendix + wellNameVersion + ' ' + sampleNameVersion + ' ' + reportConstants.viscosityCambridgeEMV, marginSize, tableStartY);
            tableStartY += 5;

            doc.setFontSize(10);
            doc.setFont(reportConstants.eMprintCondensedRegular);
            doc.text(reportConstants.temperature + ': ' + viscosityReport.viscosityData[0].temperature + this._reportGeneralUtilityService.ToSuperscript(0) + reportConstants.fChar, marginSize, tableStartY);

            tableStartY += 10;

            autoTable(doc, {
                head: [
                    [reportConstants.pressurePsia, reportConstants.viscosityCP]
                ],
                body: viscosityReport.viscosityData.map((val: ViscosityData) => [val.pressure ?? '', val.pcv ?? '']),
                startY: Math.round(tableStartY),
                theme: reportConstants.plain,
                margin: { left: marginSize + 40, right: marginSize },
                styles: this.commonStylesWithoutBorder as Partial<Styles>,
                headStyles: {
                    fontSize: 10,
                    halign: reportConstants.center,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    fontStyle: reportConstants.normal,
                    font: reportConstants.eMprintCondensedBold,
                    cellPadding: { top: 0.45, bottom: 0.45 },
                    lineWidth: 0
                },
                bodyStyles: {
                    fontSize: 8,
                    halign: reportConstants.center,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    font: reportConstants.eMprintCondensedRegular,
                    cellPadding: { top: 0.45, bottom: 0.45 },
                    lineWidth: 0
                },
                columnStyles: {
                    0: { cellWidth: 30 },
                    1: { cellWidth: 30 }
                },

                didParseCell: function (data) {
                    if (data.section === reportConstants.body && typeof data.cell.raw === 'number') {
                        data.cell.text = [data.cell.raw.toFixed(data.column.index === 0 ? 0 : 3)];
                    }
                },

                willDrawCell: function (data) {
                    const { row, doc, section } = data;

                    if (section === reportConstants.body) {
                        const pressure = (row.raw as [number, number])[0];

                        if (pressure === viscosityReport.satP) {
                            doc.setTextColor(192, 0, 0);
                        } else if (pressure === viscosityReport.resP) {
                            doc.setTextColor(0, 176, 80);
                        } else {
                            doc.setTextColor(0, 0, 0);
                        }
                    }
                },

                didDrawCell: function (data) {
                    const { cell, row, column, doc, section } = data;
                    if (section === reportConstants.head) {
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;

                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);

                        if (row.index === 0) {
                            doc.line(x, y, x + w, y);
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        } else if (row.index === 3) {
                            doc.line(x, y + h - 0.5, x + w, y + h - 0.5);
                            doc.line(x, y + h, x + w, y + h);
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);

                        } else {
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        }
                    } else {
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.rect(cell.x, cell.y, cell.width, cell.height);

                        if (section === reportConstants.body) {
                            const pressure = (row.raw as [number, number])[0];

                            if (pressure === viscosityReport.satP) {
                                if (column.index === 0) {
                                    doc.setFontSize(8);
                                    doc.setFont(reportConstants.eMprintCondensedBold);
                                    doc.setTextColor(0, 0, 0);
                                    doc.text(reportConstants.satPNoSpace, cell.x - 8, cell.y + cell.height / 2);
                                }

                            } else if (pressure === viscosityReport.resP) {

                                if (column.index === 0) {
                                    doc.setFontSize(8);
                                    doc.setFont(reportConstants.eMprintCondensedBold);
                                    doc.setTextColor(0, 0, 0);
                                    doc.text(reportConstants.resp, cell.x - 8, cell.y + cell.height / 2);
                                }

                            }
                        }

                    }
                }
            });

            const finalY = doc.lastAutoTable?.finalY || tableStartY + 20;
            doc.setFontSize(8);
            doc.setFont(reportConstants.eMprintCondensedRegular);
            doc.setTextColor(0, 0, 0);
            doc.text(reportConstants.viscosityAtResP, marginSize + 40, finalY + 10);

            if (viscosityReport?.graphData?.length > 0) {
                doc.addPage();

                doc.setFont(reportConstants.eMprintCondensedBold);
                let currentY = marginSize + 10;
                doc.setFontSize(14);
                doc.setFont(reportConstants.eMprintCondensedBold);
                doc.text(appendix + wellNameVersion + ' ' + sampleNameVersion + ' ' + reportConstants.viscosityCambridgeEMV, marginSize, currentY);
                currentY += 5;
                doc.setFontSize(10);
                doc.setFont(reportConstants.eMprintCondensedRegular);
                doc.text(reportConstants.temperature + ': ' + viscosityReport.viscosityData[0].temperature + this._reportGeneralUtilityService.ToSuperscript(0) + reportConstants.fChar, marginSize, currentY);
                currentY += 10;

                const pageWidth = doc.internal.pageSize.getWidth();
                const imageWidth = pageWidth * 0.8;

                const xPos = (pageWidth - imageWidth) / 2;
                const yPos = currentY - 5;

                const pdfW = 150;
                const pdfH = 115;

                doc.addImage(
                    viscosityReport.graphData[0],
                    reportConstants.jpeg,
                    xPos + 7,
                    yPos,
                    pdfW,
                    pdfH,
                    undefined,
                    'FAST'
                );
            }

        }
    }

    async GenerateViscosity(workbook: Workbook, worksheetName: string, viscosityReport: viscosityReports, wellName: string, sampleName: string, subHeader: string) {
        try {
            const viscosityWorksheet = workbook.addWorksheet(worksheetName);

            const borderStyle: Partial<Borders> = {
                top: { style: reportConstants.thin },
                left: { style: reportConstants.thin },
                bottom: { style: reportConstants.thin },
                right: { style: reportConstants.thin }
            };

            viscosityWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(viscosityWorksheet, undefined, {
                paperSize: undefined, // letter
                orientation: reportConstants.landscape,
                fitToPage: false,
                fitToWidth: 0,
                fitToHeight: 0,
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                },
                scale: 100
            });

            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

            viscosityWorksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });
            viscosityWorksheet.addImage(exxonLogoId, {
                tl: { col: 15, row: 0 },
                ext: { width: 151, height: 31 }
            });

            this._reportGeneralUtilityService.AddSubHeaderToWorksheet(viscosityWorksheet, subHeader, reportConstants.c1, reportConstants.k1);
            this._reportGeneralUtilityService.AddSubHeaderToWorksheet(viscosityWorksheet, subHeader, reportConstants.r1, reportConstants.y1);

            /*Merged cells C5 to J6 for report title */
            viscosityWorksheet.mergeCells(reportConstants.a4, reportConstants.k4);
            /*Bind Cell C5 for report title */
            const textReportTitleCell = viscosityWorksheet.getCell(reportConstants.a4);
            textReportTitleCell.value = wellName + ' ' + sampleName + ' ' + reportConstants.viscosityCambridgeEMV;
            textReportTitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            textReportTitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            /*Merged cells C7 to F8 for report subtitle */
            viscosityWorksheet.mergeCells(reportConstants.a5, reportConstants.e5);
            /*Bind Cell C7 for report subtitle */
            const textReportSubtitleCell = viscosityWorksheet.getCell(reportConstants.a5);
            textReportSubtitleCell.value = reportConstants.temperature + ': ' + viscosityReport.viscosityData[0].temperature + this._reportGeneralUtilityService.ToSuperscript(0) + reportConstants.fChar;
            textReportSubtitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: false };
            textReportSubtitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            /*Merged cells C5 to J6 for report title */
            viscosityWorksheet.mergeCells(reportConstants.p4, reportConstants.z4);
            /*Bind Cell C5 for report title */
            const textReportTitleCell2 = viscosityWorksheet.getCell(reportConstants.p4);
            textReportTitleCell2.value = wellName + ' ' + sampleName + ' ' + reportConstants.viscosityCambridgeEMV;
            textReportTitleCell2.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            textReportTitleCell2.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            /*Merged cells C7 to F8 for report subtitle */
            viscosityWorksheet.mergeCells(reportConstants.p5, reportConstants.z5);
            /*Bind Cell C7 for report subtitle */
            const textReportSubtitleCell2 = viscosityWorksheet.getCell(reportConstants.p5);
            textReportSubtitleCell2.value = reportConstants.temperature + ': ' + viscosityReport.viscosityData[0].temperature + this._reportGeneralUtilityService.ToSuperscript(0) + reportConstants.fChar;
            textReportSubtitleCell2.font = { name: reportConstants.eMprintCondensed, size: 14, bold: false };
            textReportSubtitleCell2.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            viscosityWorksheet.getRow(4).height = 20;
            viscosityWorksheet.getRow(5).height = 20;
            // Add table for Flashed Liquid, Flashed Gas, Whole Fluid
            const tableStartRow = 8;

            // Increase row height for the header row for better visibility
            viscosityWorksheet.getRow(tableStartRow).height = 20;
            viscosityWorksheet.getRow(tableStartRow + 1).height = 20;

            // Header rows
            /*Merged cells C10 to D10 for Pressure (psia) values*/
            viscosityWorksheet.mergeCells(`C${tableStartRow}`, `D${tableStartRow}`);
            /*Bind Cell C10 for Pressure (psia) values */
            const textFlashedLiquidCell = viscosityWorksheet.getCell(`C${tableStartRow}`);
            textFlashedLiquidCell.value = reportConstants.pressurePsia;
            textFlashedLiquidCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textFlashedLiquidCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textFlashedLiquidCell.border = borderStyle;

            /*Merged cells E10 to F10 for Viscosity (cP) values*/
            viscosityWorksheet.mergeCells(`E${tableStartRow}`, `F${tableStartRow}`);
            /*Bind Cell F10 for Viscosity (cP) values */
            const textFlashedGasCell = viscosityWorksheet.getCell(`E${tableStartRow}`);
            textFlashedGasCell.value = reportConstants.viscosityCP;
            textFlashedGasCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textFlashedGasCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textFlashedGasCell.border = borderStyle;

            for (let i = 0; i < viscosityReport.viscosityData.length; i++) {
                /*Merged cells C11 to D11 for Pressure (psia) values*/
                viscosityWorksheet.mergeCells(`C${tableStartRow + 1 + i}`, `D${tableStartRow + 1 + i}`);
                /*Bind Cell E11 for Pressure (psia) values */
                const textPressureCell = viscosityWorksheet.getCell(`C${tableStartRow + 1 + i}`);
                const pressure = viscosityReport.viscosityData[i].pressure;
                setNumericOrTextCellValue(textPressureCell, typeof pressure === 'number' ? pressure.toFixed(0) : pressure ?? '');
                textPressureCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
                textPressureCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                textPressureCell.border = borderStyle;

                /*Merged cells E11 to F11 for Viscosity (cP) values*/
                viscosityWorksheet.mergeCells(`E${tableStartRow + 1 + i}`, `F${tableStartRow + 1 + i}`);
                /*Bind Cell G11 for Viscosity (cP) values */
                const textViscosityCell = viscosityWorksheet.getCell(`E${tableStartRow + 1 + i}`);
                const pcv = viscosityReport.viscosityData[i].pcv;
                setNumericOrTextCellValue(textViscosityCell, typeof pcv === 'number' ? pcv.toFixed(3) : pcv ?? '');
                textViscosityCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
                textViscosityCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                textViscosityCell.border = borderStyle;

                if (viscosityReport.viscosityData[i].pressure === viscosityReport.satP) {
                    /*Bind Cell B11 for SatP */
                    const satPCell = viscosityWorksheet.getCell(`B${tableStartRow + 1 + i}`);
                    satPCell.value = reportConstants.satPNoSpace;
                    satPCell.font = { name: reportConstants.eMprintCondensed, size: 10, bold: true };
                    satPCell.alignment = { horizontal: reportConstants.right, vertical: reportConstants.middle };
                    textPressureCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false, color: { argb: reportConstants.ffc00000 } };
                    textViscosityCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false, color: { argb: reportConstants.ffc00000 } };
                }
                else if (viscosityReport.viscosityData[i].pressure === viscosityReport.resP) {
                    /*Bind Cell D11 for ResP */
                    const resPCell = viscosityWorksheet.getCell(`B${tableStartRow + 1 + i}`);
                    resPCell.value = reportConstants.resp;
                    resPCell.font = { name: reportConstants.eMprintCondensed, size: 10, bold: true };
                    resPCell.alignment = { horizontal: reportConstants.right, vertical: reportConstants.middle };
                    textPressureCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false, color: { argb: reportConstants.ff00b050 } };
                    textViscosityCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false, color: { argb: reportConstants.ff00b050 } };
                }
            }

            //Implement below conde once graph data is available

            const graphImageId = workbook.addImage({
                base64: viscosityReport.graphData[0],
                extension: reportConstants.png,
            });


            viscosityWorksheet.addImage(graphImageId, {
                tl: { col: 16, row: 6 },
                ext: { width: 600, height: 500 }
            });

            /*Merged cells B11 to G11 for Viscosity note */
            viscosityWorksheet.mergeCells(`B${tableStartRow + 2 + viscosityReport.viscosityData.length}`, `I${tableStartRow + 2 + viscosityReport.viscosityData.length}`);
            /*Bind Cell B11 for Viscosity note */
            const textViscosityCell = viscosityWorksheet.getCell(`B${tableStartRow + 2 + viscosityReport.viscosityData.length}`);
            textViscosityCell.value = reportConstants.viscosityAtResP;
            textViscosityCell.font = { name: reportConstants.eMprintCondensed, size: 10, bold: false };
            textViscosityCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }
    }
}